import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Brain, ArrowLeft, CheckCircle, Mail, Lock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { trackEvent } from '../components/GoogleAnalytics';
import { useSignupConsents } from '../hooks/useConsent';
import { useFormValidation } from '../hooks/useFormValidation';
import { useTranslation } from 'react-i18next';

export default function SignupPage() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const { errors, validateField, validateAll } = useFormValidation<{
    email: string;
    password: string;
    confirmPassword: string;
  }>({
    email: (value) => {
      if (!value) return t('validation.emailRequired');
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return t('validation.emailInvalid');
      return null;
    },
    password: (value, values) => {
      if (!value) return t('validation.passwordRequired');
      if (value.length < 6) return t('signup.minChars');
      return null;
    },
    confirmPassword: (value, values) => {
      if (!value) return t('signup.confirmPlaceholder');
      if (value !== values.password) return t('signup.errors.passwordMismatch');
      return null;
    },
  });
  // GDPR Consent management
  const { consents, setConsentValue, areRequiredComplete, getMissingLabels } = useSignupConsents();
  const { signUp } = useAuth();
  const navigate = useNavigate();

  // Remove dashboard-active class to ensure scroll works
  useEffect(() => {
    document.body.classList.remove('dashboard-active');
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.height = '';

    return () => {
      document.body.classList.remove('dashboard-active');
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Run all validations
    const isValid = validateAll({ email, password, confirmPassword });
    if (!isValid) {
      return;
    }

    // Validate GDPR consents
    if (!areRequiredComplete) {
      const missing = getMissingLabels();
      setError(t('signup.errors.missingConsents', { missing: missing.join(', ') }));
      return;
    }

    setLoading(true);

    const { error, needsEmailConfirmation } = await signUp(email, password, consents);

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      // Track successful signup
      trackEvent('sign_up', {
        method: 'email'
      });
      setNeedsConfirmation(needsEmailConfirmation);
      setSuccess(true);
      setLoading(false);
      // Only auto-redirect if the user is already signed in (email confirmation disabled).
      // If email confirmation is enabled, needsEmailConfirmation=true and there is no active
      // session yet — redirecting to /dashboard would just bounce them back to /login.
      if (!needsEmailConfirmation) {
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    }
  };

  if (success) {
    return (
      <>
        <SEO
          title={t('signup.successTitle')}
          description={t('signup.seoSuccessDescription')}
          canonical="/signup"
          noindex={true}
        />
        <div className="min-h-screen bg-gradient-hero relative overflow-hidden flex items-center justify-center p-4">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, hsl(262.1 83.3% 57.8%) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full relative z-10"
        >
          <div className="bg-card rounded-2xl shadow-card border border-border/50 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">{t('signup.successTitle')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('signup.successMessage', { email })}
            </p>
            {needsConfirmation ? (
              // Email confirmation required: tell the user to check their inbox
              <>
                <p className="text-sm text-muted-foreground mb-6">
                  {t('forgotPassword.checkSpam')}
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-primary hover:text-primary/80 font-medium"
                >
                  {t('login.submit')} →
                </button>
              </>
            ) : (
              // No email confirmation: user is already signed in, redirect to dashboard
              <>
                <p className="text-sm text-muted-foreground">
                  {t('signup.successDescription')}
                </p>
                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('signup.redirecting')}
                </div>
              </>
            )}
          </div>
        </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={t('signup.title')}
        description={t('signup.seoDescription')}
        canonical="/signup"
      />
      <div className="min-h-screen bg-gradient-hero relative overflow-hidden flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, hsl(262.1 83.3% 57.8%) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full relative z-10"
      >
        {/* Signup Card */}
        <div className="bg-card rounded-2xl shadow-card border border-border/50 p-6">
          {/* Badge */}
          <div className="flex items-center justify-between mb-4">
            <Link
              to="/"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="bg-secondary/10 border border-secondary/20 rounded-full px-4 py-1.5 inline-flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-secondary"></div>
              <span className="text-sm font-semibold text-secondary">{t('signup.title')}</span>
            </div>
          </div>

          {/* Header with Logo */}
          <div className="text-center mb-6">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2.5 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center shadow-soft">
                <Brain className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-2xl font-bold text-foreground">Psymetrika</span>
            </button>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
              role="alert"
              id="signup-error"
            >
              <p className="text-red-800 text-sm">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    const nextEmail = e.target.value;
                    setEmail(nextEmail);
                    if (errors.email) {
                      validateField('email', nextEmail, { email: nextEmail, password, confirmPassword });
                    }
                  }}
                  onBlur={(e) => validateField('email', e.target.value, { email: e.target.value, password, confirmPassword })}
                  required
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-background text-foreground ${errors.email ? 'border-red-500' : 'border-border'}`}
                  placeholder="tu@email.com"
                />
              </div>
              {errors.email && (
                <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                {t('signup.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    const nextPassword = e.target.value;
                    setPassword(nextPassword);
                    if (errors.password) {
                      validateField('password', nextPassword, { email, password: nextPassword, confirmPassword });
                    }
                    if (confirmPassword || errors.confirmPassword) {
                      validateField('confirmPassword', confirmPassword, { email, password: nextPassword, confirmPassword });
                    }
                  }}
                  onBlur={(e) => validateField('password', e.target.value, { email, password: e.target.value, confirmPassword })}
                  required
                  minLength={6}
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-background text-foreground ${errors.password ? 'border-red-500' : 'border-border'}`}
                  placeholder={t('signup.passwordPlaceholder')}
                />
              </div>
              {errors.password && (
                <p id="password-error" className="mt-1 text-sm text-red-600" role="alert">{errors.password}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                {t('signup.confirmPassword')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    const nextConfirm = e.target.value;
                    setConfirmPassword(nextConfirm);
                    if (errors.confirmPassword) {
                      validateField('confirmPassword', nextConfirm, { email, password, confirmPassword: nextConfirm });
                    }
                  }}
                  onBlur={(e) => validateField('confirmPassword', e.target.value, { email, password, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                  aria-invalid={!!errors.confirmPassword}
                  aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-background text-foreground ${errors.confirmPassword ? 'border-red-500' : 'border-border'}`}
                  placeholder={t('signup.confirmPlaceholder')}
                />
              </div>
              {errors.confirmPassword && (
                <p id="confirm-password-error" className="mt-1 text-sm text-red-600" role="alert">{errors.confirmPassword}</p>
              )}
            </div>

            {/* GDPR Consent Checkboxes */}
            <div className="space-y-3 pt-2">
              {/* Terms of Service */}
              <label className="flex items-start gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consents.terms}
                  onChange={(e) => setConsentValue('terms', e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-primary border-border rounded focus:ring-primary"
                />
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  {t('signup.acceptTerms')}{' '}
                  <Link to="/terms" className="text-primary hover:underline" target="_blank">
                    {t('signup.termsOfService')}
                  </Link>
                  <span className="text-red-500">*</span>
                </span>
              </label>

              {/* Privacy Policy */}
              <label className="flex items-start gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consents.privacy}
                  onChange={(e) => setConsentValue('privacy', e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-primary border-border rounded focus:ring-primary"
                />
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  {t('signup.acceptPrivacy')}{' '}
                  <Link to="/privacy" className="text-primary hover:underline" target="_blank">
                    {t('signup.privacyPolicy')}
                  </Link>
                  <span className="text-red-500">*</span>
                </span>
              </label>

              {/* Marketing (Optional) */}
              <label className="flex items-start gap-2.5 cursor-pointer group border-t border-border/50 pt-3 mt-3">
                <input
                  type="checkbox"
                  checked={consents.marketing}
                  onChange={(e) => setConsentValue('marketing', e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-primary border-border rounded focus:ring-primary"
                />
                <div className="text-sm">
                  <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                    {t('signup.marketingConsent')}
                  </span>
                  <span className="text-xs text-muted-foreground/60 ml-1">({t('signup.optional')})</span>
                </div>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              variant="gradient"
              className="w-full"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('signup.loading')}
                </>
              ) : (
                t('signup.submitFree')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              {t('signup.haveAccount')}{' '}
              <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
                {t('signup.logIn')}
              </Link>
            </p>
          </div>
        </div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-4 text-center text-sm text-muted-foreground"
        >
          <div className="flex items-center gap-2 bg-card/50 backdrop-blur-sm px-4 py-2 rounded-full border border-border/50">
            <CheckCircle className="w-4 h-4 text-secondary" />
            <span>{t('signup.features.freeTests')}</span>
          </div>
          <div className="flex items-center gap-2 bg-card/50 backdrop-blur-sm px-4 py-2 rounded-full border border-border/50">
            <CheckCircle className="w-4 h-4 text-secondary" />
            <span>{t('signup.features.aiAnalysis')}</span>
          </div>
          <div className="flex items-center gap-2 bg-card/50 backdrop-blur-sm px-4 py-2 rounded-full border border-border/50">
            <CheckCircle className="w-4 h-4 text-secondary" />
            <span>{t('signup.features.availableTests')}</span>
          </div>
        </motion.div>
      </motion.div>
      </div>
    </>
  );
}
